// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Zone from "@insite/client-framework/Components/Zone";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { xGoogleAnalyticsState } from "@sc/App/GoogleAnalyticsConnector/Store/Reducers";
import { getDataLayer } from "@sc/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import { CustomAppState } from "@sc/App/Store/Reducers";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const PENDING_LOGIN_EVENT_KEY = "pendingLoginEvent";

const mapStateToProps = (state: CustomAppState) => {
    const session = getSession(state);
    const billToState = getBillToState(state, session.billToId);
    const shipToState = getShipToState(state, session.shipToId);
    const xGoogleAnalytics = (state as unknown as xGoogleAnalyticsState).xGoogleAnalytics;

    return {
        // XNG-Change
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        billTo: billToState?.value,
        shipTo: shipToState?.value,
        shipToId: session.shipToId,
        xGoogleAnalytics,
    };
};

export interface FooterStyles {
    page?: MobiusPageProps;
}

type Props = PageProps & ReturnType<typeof mapStateToProps>;

export const footerStyles: FooterStyles = {};
const styles = footerStyles;

// XNG-Change: structure
const Footer = ({ id, isSignInPage, billTo, shipTo, shipToId, xGoogleAnalytics }: Props) => {
    // XNG-Change: GA4 Login Event
    React.useEffect(() => {
        if (!xGoogleAnalytics.loginEvent) {
            return;
        }

        const pendingLoginEvent =
            typeof window !== "undefined" ? window.localStorage.getItem(PENDING_LOGIN_EVENT_KEY) === "true" : false;

        if (!pendingLoginEvent || !billTo) {
            return;
        }

        if (shipTo) {
            const dataLayer = getDataLayer();

            if (dataLayer) {
                const googleAnalyticsData = {
                    event: xGoogleAnalytics.loginEventName,
                    affiliation: billTo.companyName || "",
                    sales_rep: xGoogleAnalytics.salesRep || "",
                    is_rep: xGoogleAnalytics.isRep || "",
                    bill_to_num: billTo?.customerNumber || "",
                    ship_to_num: shipTo?.customerNumber || "",
                    cust_type: xGoogleAnalytics.customerType || "",
                    login_type: xGoogleAnalytics.loginType || "",
                    warehouse: xGoogleAnalytics.warehouse || "",
                };

                dataLayer.push({ ecommerce: null });
                dataLayer.push(googleAnalyticsData);
            }

            if (typeof window !== "undefined") {
                window.localStorage.removeItem(PENDING_LOGIN_EVENT_KEY);
            }
        }
    }, [billTo, shipTo, shipToId, xGoogleAnalytics.loginEvent]);

    return (
        <Page
            as="footer"
            isFooter={true}
            {...styles.page}
            css={css`
                ${styles.page?.css};
                ${isSignInPage ? "margin-top: 0;" : ""};
            `}
        >
            <Zone contentId={id} zoneName="Content" />
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps)(Footer),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "Footer" instead of this constant.
 */
export const FooterContext = "Footer";
